import * as React from "react";

import {
  AI_ANALYSIS_JOB_POLL_INTERVAL_MS,
  fetchAiAnalysisJob,
  formatAiAnalysisJobProgress,
  isAbortError,
  isAiAnalysisJobTerminal,
  type AiAnalysisJobSnapshot,
} from "../../../agreement";

export type AiAnalysisJobPollHandlers = {
  onTick: (job: AiAnalysisJobSnapshot) => void | Promise<void>;
  onTerminal: (job: AiAnalysisJobSnapshot) => void | Promise<void>;
  onError: (err: unknown) => void;
};

/**
 * Poll a contract AI analysis job until it reaches a terminal status.
 * Survives tab switches; stop via returned ``stop`` or by clearing ``jobId``.
 */
export function useAiAnalysisJobPolling(): {
  startPolling: (jobId: string, handlers: AiAnalysisJobPollHandlers) => void;
  stopPolling: () => void;
  isPolling: boolean;
} {
  const intervalRef = React.useRef<number | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const handlersRef = React.useRef<AiAnalysisJobPollHandlers | null>(null);
  const jobIdRef = React.useRef<string | null>(null);
  const [isPolling, setIsPolling] = React.useState(false);

  const stopPolling = React.useCallback(() => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    jobIdRef.current = null;
    handlersRef.current = null;
    setIsPolling(false);
  }, []);

  const pollOnce = React.useCallback(async () => {
    const jobId = jobIdRef.current;
    const handlers = handlersRef.current;
    if (!jobId || !handlers) {
      return;
    }
    const ac = new AbortController();
    abortRef.current?.abort();
    abortRef.current = ac;
    try {
      const job = await fetchAiAnalysisJob(jobId, ac.signal);
      if (ac.signal.aborted) {
        return;
      }
      await handlers.onTick(job);
      if (isAiAnalysisJobTerminal(job.status)) {
        stopPolling();
        await handlers.onTerminal(job);
      }
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) {
        return;
      }
      stopPolling();
      handlers.onError(err);
    }
  }, [stopPolling]);

  const startPolling = React.useCallback(
    (jobId: string, handlers: AiAnalysisJobPollHandlers) => {
      stopPolling();
      jobIdRef.current = jobId;
      handlersRef.current = handlers;
      setIsPolling(true);
      void pollOnce();
      intervalRef.current = window.setInterval(() => {
        void pollOnce();
      }, AI_ANALYSIS_JOB_POLL_INTERVAL_MS);
    },
    [pollOnce, stopPolling]
  );

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  return { startPolling, stopPolling, isPolling };
}

export { formatAiAnalysisJobProgress };
